import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Plus, Trash2, Search, Percent, Mail, Phone, MapPin, Edit, FileText, IndianRupee, CalendarDays, User, ChevronDown, ChevronUp, Bold, Italic, Underline, Code, Pencil, Type, Braces, X, RefreshCw } from 'lucide-react';
import { getSession } from '../../getSession';
import { getProductsByName, getCountryList, getStateListByCountryId, getCityListByStateId, getSettingBySection, getLeadSuggectionById, getInvoiceCurrencyList, getProductForSuggetion, insertNewQuote, isQuotationNumberAlreadyExists, getLeadCustomerSuggection, getTaxesForSuggetion } from '../../utils/lead';
import AddProductForm from './AddProductForm';
import Input from '../common/Input';
import Select from '../common/Select';
import Button from '../common/Button';
import Textarea from '../common/Textarea';
import Swal from 'sweetalert2';

const getFirstField = (val) => {
    if (!val) return '';
    return String(val).split(',')[0].trim();
};

const stripCountryCode = (phone) => {
    if (!phone) return '';
    const sPhone = String(phone).trim();
    return sPhone.replace(/^(\+\d{1,3}\s?|\d{1,3}-)/, '');
};

const itemPreviewHandler = (message) => {
    if (!message || message.length === 0) {
        return '<span class="text-gray-400 italic">No description</span>';
    }
    let rendered = message.replace(/\n/g, '<br/>');
    rendered = rendered.replace(/(?:\*)(?:(?!\s))((?:(?!\*|\n).)+)(?:\*)/g, '<b>$1</b>')
        .replace(/(?:_)(?:(?!\s))((?:(?!\n|_).)+)(?:_)/g, '<u>$1</u>')
        .replace(/(?:~)(?:(?!\s))((?:(?!\n|~).)+)(?:~)/g, '<s>$1</s>')
        .replace(/(?:--)(?:(?!\s))((?:(?!\n|--).)+)(?:--)/g, '<i>$1</i>')
        .replace(/(?:```)(?:(?!\s))((?:(?!\n|```).)+)(?:```)/g, '<code class="bg-gray-100 px-1 rounded">$1</code>');
    return rendered;
};

const AddQuotationForm = React.forwardRef(({ lead }, ref) => {
    const { userId, TokenId, parentId } = getSession();

    const [formData, setFormData] = useState({
        logo: getSession().Logo || '',
        quotationTitle: 'Quotation',
        quotationSubHeading: '',
        placeOfSupply: '',
        placeOfOrigin: lead?.City || '',
        companyName: 'pel softlabs pvt. ltd.',
        companyEmail: 'testsales@email19.in',
        companyPhone: '7011677851',
        companyAddress: '305-306 Vardhman Chambers 2, Community Center (Near PVR Cinema), New, Block G, Vnew, , Badakhshan, Afghanistan,',
        taxNumber: '27AAAPA1234A1Z5',
        panNumber: 'AAAPA1234A',
        quotationNumber: '',
        quotationDate: new Date().toISOString().split('T')[0],
        validTillDate: new Date().toISOString().split('T')[0],
        currency: 'INR',
        notes: '',
        termsAndConditions: '',
        footer: '',
        items: [],
        subTotal: 0,
        totalTax: 0,
        grandTotal: 0,
        billTo: {
            name: lead?.PersonName || lead?.Name || '',
            email: getFirstField(lead?.EmailID || lead?.EmailId || lead?.Email || ''),
            phone: stripCountryCode(getFirstField(lead?.MobileNo || lead?.Phone || '')),
            address: lead?.ResidentialAddress || lead?.Address || '',
            city: lead?.City || '',
            state: lead?.State || '',
            country: lead?.Country || '',
            pincode: lead?.PinCode || '',
            taxNumber: lead?.TaxNumber || '',
            panNumber: lead?.PanNumber || ''
        },
        shipTo: {
            name: lead?.PersonName || lead?.Name || '',
            email: getFirstField(lead?.EmailID || lead?.EmailId || lead?.Email || ''),
            phone: stripCountryCode(getFirstField(lead?.MobileNo || lead?.Phone || '')),
            address: lead?.ResidentialAddress || lead?.Address || '',
            city: lead?.City || '',
            state: lead?.State || '',
            country: lead?.Country || '',
            pincode: lead?.PinCode || ''
        },
        sameAsBillTo: true
    });

    const [isCompanyOpen, setIsCompanyOpen] = useState(false);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [isShipToModalOpen, setIsShipToModalOpen] = useState(false);
    const [editAddressData, setEditAddressData] = useState({});
    const [productSuggestions, setProductSuggestions] = useState([]);
    const [activeItemIndex, setActiveItemIndex] = useState(null);
    const [isFooterOpen, setIsFooterOpen] = useState(true);
    const suggestionRef = useRef(null);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    const [itemSearchText, setItemSearchText] = useState('');
    const [itemSuggestions, setItemSuggestions] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [showItemSearch, setShowItemSearch] = useState(false);
    const [selectedProductForVariant, setSelectedProductForVariant] = useState(null);
    const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [showCustomerSearch, setShowCustomerSearch] = useState(false);
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [taxSearchText, setTaxSearchText] = useState('');
    const [taxSuggestions, setTaxSuggestions] = useState([]);
    const [isLoadingTaxes, setIsLoadingTaxes] = useState(false);
    const [activeTaxSearchIndex, setActiveTaxSearchIndex] = useState(null);

    const [isDescEditModalOpen, setIsDescEditModalOpen] = useState(false);
    const [editingDescIndex, setEditingDescIndex] = useState(null);
    const [descTempText, setDescTempText] = useState('');
    const [isAddProductOpen, setIsAddProductOpen] = useState(false);

    // Stores newly created products so they always appear in subsequent searches
    const recentlyAddedProductsRef = useRef([]);
    const [placeholderOptions] = useState([
        'PersonName', 'EmailID', 'MobileNo', 'City', 'State', 'Country', 'Address', 'PinCode', 'CompanyName', 'TaxNumber'
    ]);

    const [isCkEditorLoaded, setIsCkEditorLoaded] = useState(false);

    useEffect(() => {
        const scriptId = 'ckeditor-script';
        if (document.getElementById(scriptId)) {
            if (window.CKEDITOR) setIsCkEditorLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = '/assets/layout/plugin/ckeditor4-master/ckeditor.js';
        script.async = true;
        script.onload = () => {
            setIsCkEditorLoaded(true);
        };
        script.onerror = () => {
            console.error('Failed to load CKEditor script');
        };
        document.body.appendChild(script);
    }, []);

    useEffect(() => {
        const CK = window.CKEDITOR;
        if (!isCkEditorLoaded || !CK || !CK.instances) return;

        const config = {
            height: 150,
            removeButtons: 'About,Maximize',
            versionCheck: false
        };

        const editors = [
            { id: 'termsEditorQ', field: 'termsAndConditions' },
            { id: 'notesEditorQ', field: 'notes' }
        ];

        const activeEditors = [];

        editors.forEach(({ id, field }) => {
            if (document.getElementById(id)) {
                try {
                    if (CK.instances[id]) {
                        CK.instances[id].destroy(true);
                    }
                    const editor = CK.replace(id, config);
                    editor.on('instanceReady', () => {
                        editor.setData(formData[field] || '');
                    });
                    editor.on('change', () => {
                        setFormData(prev => ({ ...prev, [field]: editor.getData() }));
                    });
                    activeEditors.push(editor);
                } catch (e) {
                    console.warn(`CKEditor init error for ${id}:`, e);
                }
            }
        });

        let footerEditor = null;
        if (isFooterOpen && document.getElementById('footerEditorQ')) {
            try {
                if (CK.instances['footerEditorQ']) {
                    CK.instances['footerEditorQ'].destroy(true);
                }
                footerEditor = CK.replace('footerEditorQ', config);
                footerEditor.on('instanceReady', () => {
                    footerEditor.setData(formData.footer || '');
                });
                footerEditor.on('change', () => {
                    setFormData(prev => ({ ...prev, footer: footerEditor.getData() }));
                });
            } catch (e) {
                console.warn('CKEditor footer init error:', e);
            }
        }

        return () => {
            activeEditors.forEach(ed => {
                try { if (ed && typeof ed.destroy === 'function') ed.destroy(); } catch (e) { }
            });
            try { if (footerEditor && typeof footerEditor.destroy === 'function') footerEditor.destroy(); } catch (e) { }
        };
    }, [isCkEditorLoaded, isFooterOpen]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const list = await getCountryList({});
                setCountries(Array.isArray(list) ? list : []);
            } catch (err) {
                console.warn('fetchCountries error', err);
            }
        };
        fetchCountries();
    }, []);

    const handleModalCountryChange = async (e) => {
        const val = e.target.value;
        const countryName = e.target.options[e.target.selectedIndex].text;
        setEditAddressData(prev => ({ ...prev, countryId: val, country: countryName, stateId: '', state: '', cityId: '', city: '' }));
        setStates([]);
        setCities([]);
        if (val) {
            try {
                const st = await getStateListByCountryId({ Details: { CountryId: val } });
                setStates(Array.isArray(st) ? st : []);
            } catch (err) {
                console.warn('fetch states error', err);
            }
        }
    };

    const handleModalStateChange = async (e) => {
        const val = e.target.value;
        const stateName = e.target.options[e.target.selectedIndex].text;
        setEditAddressData(prev => ({ ...prev, stateId: val, state: stateName, cityId: '', city: '' }));
        setCities([]);
        if (val) {
            try {
                const ct = await getCityListByStateId({ Details: { StateId: val } });
                setCities(Array.isArray(ct) ? ct : []);
            } catch (err) {
                console.warn('fetch cities error', err);
            }
        }
    };

    useEffect(() => {
        const initializeSettings = async () => {
            try {
                const [companySettings, quotationSettings, currencyList] = await Promise.all([
                    getSettingBySection({ UserId: userId, Section: 'company' }),
                    getSettingBySection({ UserId: userId, Section: 'quotation' }),
                    getInvoiceCurrencyList()
                ]);

                setCurrencies(currencyList);

                const getVal = (list, key) => list.find(s => s.Key?.toLowerCase() === key.toLowerCase())?.Value || '';

                const companyData = {
                    companyName: getVal(companySettings, 'LegalName'),
                    companyEmail: getVal(companySettings, 'Email'),
                    companyPhone: getVal(companySettings, 'Phone'),
                    companyAddress: getVal(companySettings, 'HeadOffice'),
                    taxNumber: getVal(companySettings, 'TaxNumber'),
                    panNumber: getVal(companySettings, 'PanNumber'),
                    logo: getVal(companySettings, 'HttpLogo') || getVal(companySettings, 'Logo'),
                    placeOfOrigin: getVal(companySettings, 'State'),
                    companyCountry: getVal(companySettings, 'Country'),
                    companyCountryId: getVal(companySettings, 'CountryId'),
                    companyState: getVal(companySettings, 'State'),
                    companyStateId: getVal(companySettings, 'StateId'),
                };

                const nextNum = getVal(quotationSettings, 'NextNumber') || getVal(quotationSettings, 'StartId') || '1';
                const prefix = getVal(quotationSettings, 'NumberPrefix') || getVal(quotationSettings, 'Prefix') || '';
                const finYearFormat = getVal(quotationSettings, 'FinancialYearFormat') || (getVal(quotationSettings, 'FinancialYear') === 'True' ? 'YY-YY' : '');
                const numDigit = parseInt(getVal(quotationSettings, 'NumberDigit')) || 3;

                let finStr = '';
                const today = new Date();
                const nextYear = new Date();
                nextYear.setFullYear(today.getFullYear() + 1);

                const getYearStr = (date, format) => {
                    const yyyy = date.getFullYear();
                    const yy = String(yyyy).slice(-2);
                    return format === 'YYYY' ? yyyy : yy;
                };

                if (finYearFormat) {
                    switch (finYearFormat) {
                        case 'YY-YY': finStr = `${getYearStr(today, 'YY')}-${getYearStr(nextYear, 'YY')}`; break;
                        case 'YYYY-YYYY': finStr = `${getYearStr(today, 'YYYY')}-${getYearStr(nextYear, 'YYYY')}`; break;
                        case 'YYYY-YY': finStr = `${getYearStr(today, 'YYYY')}-${getYearStr(nextYear, 'YY')}`; break;
                        case 'YYYY/YY': finStr = `${getYearStr(today, 'YYYY')}/${getYearStr(nextYear, 'YY')}`; break;
                        case 'YY/YY': finStr = `${getYearStr(today, 'YY')}/${getYearStr(nextYear, 'YY')}`; break;
                        case 'YYYY/YYYY': finStr = `${getYearStr(today, 'YYYY')}/${getYearStr(nextYear, 'YYYY')}`; break;
                    }
                }

                const paddedNum = nextNum.padStart(numDigit, '0');
                const generatedQuotationNumber = `${prefix}${prefix && '/'}${finStr}${finStr && '/'}${paddedNum}`;

                const dueDays = parseInt(getVal(quotationSettings, 'PaymentDueDays')) || 30;
                const dueDate = new Date();
                dueDate.setDate(today.getDate() + dueDays);

                const defaultCurrencyCode = getVal(quotationSettings, 'Currency') || 'INR';

                setFormData(prev => ({
                    ...prev,
                    ...companyData,
                    quotationNumber: generatedQuotationNumber,
                    quotationTitle: getVal(quotationSettings, 'Title') || 'Quotation',
                    quotationSubHeading: getVal(quotationSettings, 'Subheading'),
                    notes: getVal(quotationSettings, 'Notes') || getVal(quotationSettings, 'Note') || '',
                    footer: getVal(quotationSettings, 'Footer') || getVal(quotationSettings, 'FooterNotes') || '',
                    termsAndConditions: getVal(quotationSettings, 'TermCondition') || getVal(quotationSettings, 'TermsAndConditions') || getVal(quotationSettings, 'Terms And Conditions') || '',
                    quotationDate: today.toISOString().split('T')[0],
                    validTillDate: dueDate.toISOString().split('T')[0],
                    currency: defaultCurrencyCode
                }));

                if (lead?.ID || lead?.LeadId || lead?.Id || lead?.id) {
                    const lId = lead?.ID || lead?.LeadId || lead?.Id || lead?.id;
                    const lResp = await getLeadSuggectionById({ LeadId: lId, UserId: userId });
                    if (lResp && lResp.length > 0) {
                        const lData = lResp[0] || lResp;
                        setFormData(prev => ({
                            ...prev,
                            billTo: {
                                ...prev.billTo,
                                name: lData.PersonName || lData.Name || prev.billTo.name,
                                phone: stripCountryCode(getFirstField(lData.MobileNo || lData.Phone || prev.billTo.phone)),
                                email: getFirstField(lData.EmailID || lData.Email || prev.billTo.email),
                                address: lData.Address || prev.billTo.address,
                                city: lData.City || prev.billTo.city,
                                state: lData.State || prev.billTo.state,
                                country: lData.Country || prev.billTo.country,
                                pincode: lData.PinCode || prev.billTo.pincode,
                                taxNumber: lData.TaxNumber || prev.billTo.taxNumber,
                                panNumber: lData.PanNumber || prev.billTo.panNumber
                            }
                        }));
                    }
                }
            } catch (err) {
                console.warn('initializeSettings error:', err);
            }
        };

        initializeSettings();
    }, [userId, TokenId, parentId, lead]);

    useEffect(() => {
        calculateTotals(formData.items);
    }, [formData.items]);

    const calculateTotals = (items = []) => {
        if (!Array.isArray(items)) return;

        let subTotal = 0;
        let totalTax = 0;
        let hasChanges = false;

        const updatedItems = items.map(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const price = parseFloat(item.price) || 0;
            const baseAmount = quantity * price;

            let discount = 0;
            if (item.isDiscountEnabled) {
                if (item.isDiscountPercentage) {
                    discount = (baseAmount * (parseFloat(item.discountPercent) || 0)) / 100;
                } else {
                    discount = parseFloat(item.discountAmount) || 0;
                }
            }

            const amountAfterDiscount = Math.max(0, baseAmount - discount);

            let tax = 0;
            if (item.isTaxEnabled) {
                tax = (amountAfterDiscount * (parseFloat(item.taxPercent) || 0)) / 100;
            }

            const finalAmount = parseFloat((amountAfterDiscount + tax).toFixed(2));
            const lineTaxAmount = parseFloat(tax.toFixed(2));

            subTotal += amountAfterDiscount;
            totalTax += tax;

            if (item.amount !== finalAmount || item.taxAmount !== lineTaxAmount || item.quantity !== quantity || item.price !== price) {
                hasChanges = true;
                return {
                    ...item,
                    quantity,
                    price,
                    amount: finalAmount,
                    taxAmount: lineTaxAmount
                };
            }
            return item;
        });

        const newSubTotal = parseFloat(subTotal.toFixed(2));
        const newTotalTax = parseFloat(totalTax.toFixed(2));
        const newGrandTotal = parseFloat((subTotal + totalTax).toFixed(2));

        if (hasChanges || formData.subTotal !== newSubTotal || formData.totalTax !== newTotalTax || formData.grandTotal !== newGrandTotal) {
            setFormData(prev => ({
                ...prev,
                items: updatedItems,
                subTotal: newSubTotal,
                totalTax: newTotalTax,
                grandTotal: newGrandTotal
            }));
        }
    };

    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleItemChange = (index, field, value) => {
        updateItem(index, field, value);

        if (field === 'name' && value.length > 2) {
            fetchProducts(value, index);
        } else if (field === 'name') {
            setProductSuggestions([]);
        }
    };

    const fetchProducts = async (name, index) => {
        try {
            const payload = {
                Token: TokenId,
                Message: "",
                LoggedUserId: userId,
                MAC_Address: "",
                IP_Address: "102.16.32.189",
                Details: {
                    productname: name,
                    parentid: parentId
                },
                BroadcastName: ""
            };
            const resp = await getProductsByName(payload);
            setProductSuggestions(resp || []);
            setActiveItemIndex(index);
        } catch (err) {
            console.error('fetchProducts error', err);
        }
    };

    const selectProduct = (index, product) => {
        const newItems = [...formData.items];
        newItems[index].name = product.ProductName || product.Text || '';
        newItems[index].price = product.SalePrice || product.Price || 0;
        newItems[index].description = product.Description || '';
        newItems[index].hsnCode = product.HSNCode || '';
        newItems[index].taxPercent = product.TaxPercent || 0;
        newItems[index].productId = product.Id || product.ProductId || 0;
        newItems[index].isVariant = product.IsVariant || 0;
        newItems[index].vCombinationJson = product.VCombinationJson || '';
        newItems[index].productJson = product.ProductJSON || '';

        setFormData(prev => ({ ...prev, items: newItems }));
        setProductSuggestions([]);
        setActiveItemIndex(null);
    };

    const addItem = () => {
        const newItem = {
            id: Date.now(),
            name: '',
            description: '',
            quantity: 1,
            price: 0,
            hsnCode: '',
            taxPercent: 0,
            taxAmount: 0,
            discountPercent: 0,
            discountAmount: 0,
            isDiscountPercentage: true,
            isDiscountEnabled: false,
            isTaxEnabled: false,
            amount: 0
        };
        const updatedItems = [...formData.items, newItem];
        setFormData({ ...formData, items: updatedItems });
    };

    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, items: newItems }));
        if (newItems.length === 0) {
            setShowItemSearch(true);
        }
    };

    const handleTaxSearch = async (index, text) => {
        setTaxSearchText(text);
        if (text.length < 1) {
            setTaxSuggestions([]);
            return;
        }
        setIsLoadingTaxes(true);
        try {
            const results = await getTaxesForSuggetion({ FilterText: text, UserId: userId });
            setTaxSuggestions(results || []);
        } catch (e) {
            console.error('Tax search error', e);
        } finally {
            setIsLoadingTaxes(false);
        }
    };

    const handleSelectTax = (itemIndex, tax) => {
        const newItems = [...formData.items];
        newItems[itemIndex] = {
            ...newItems[itemIndex],
            taxPercent: tax.Rate,
            taxName: tax.Name,
            taxId: tax.Id
        };
        setFormData(prev => ({ ...prev, items: newItems }));
        setActiveTaxSearchIndex(null);
        setTaxSearchText('');
        setTaxSuggestions([]);
    };

    const validateForm = () => {
        let strErrorMessage = '';

        const isEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        const isNumberOnly = (num) => /^\d+$/.test(num);

        if (!(lead?.ID || lead?.LeadId || lead?.Id || lead?.id)) {
            strErrorMessage += "Customer detail is required.\n";
        }

        if (!formData.billTo.name?.trim()) {
            strErrorMessage += "Name is required for bill to.\n";
        }

        if (!formData.billTo.phone?.trim()) {
            strErrorMessage += "Phone number is required.\n";
        } else if (!isNumberOnly(formData.billTo.phone)) {
            strErrorMessage += "Invalid phone number for bill to.\n";
        }

        if (formData.billTo.email && !isEmail(formData.billTo.email)) {
            strErrorMessage += "Invalid Customer EmailId for bill to.\n";
        }

        if (!formData.shipTo.name?.trim()) {
            strErrorMessage += "Name is required for ship to.\n";
        }

        if (!formData.shipTo.phone?.trim()) {
            strErrorMessage += "Phone number is required for ship to.\n";
        } else if (!isNumberOnly(formData.shipTo.phone)) {
            strErrorMessage += "Invalid phone number for ship to.\n";
        }

        if (formData.shipTo.email && !isEmail(formData.shipTo.email)) {
            strErrorMessage += "Invalid email for ship to.\n";
        }

        if (!formData.quotationNumber?.trim()) {
            strErrorMessage += "Quotation Number is required.\n";
        }

        if (!formData.items || formData.items.length === 0) {
            strErrorMessage += "Atleast one product required for quotation.\n";
        } else {
            formData.items.forEach((item, idx) => {
                if (!item.name?.trim()) {
                    strErrorMessage += `Product name is required for item ${idx + 1}.\n`;
                }
                const qty = parseFloat(item.quantity);
                if (isNaN(qty)) {
                    strErrorMessage += `Product volumn is required/invalid for item ${idx + 1}.\n`;
                } else if (qty <= 0) {
                    strErrorMessage += `Product volumn must be greater than 0 for item ${idx + 1}.\n`;
                }
                if (!item.price || isNaN(parseFloat(item.price))) {
                    strErrorMessage += `Product price is required/invalid for item ${idx + 1}.\n`;
                }
            });
        }

        if (strErrorMessage) {
            return { valid: false, message: strErrorMessage.trim() };
        }
        return { valid: true };
    };

    const getQuotationData = () => {
        return {
            QuotationId: 0,
            BillToLeadId: lead?.ID || lead?.LeadId || lead?.Id || lead?.id || 0,
            CompanyName: formData.companyName,
            CompanyEmail: formData.companyEmail,
            CompanyPhone: formData.companyPhone,
            CompanyAddress: formData.companyAddress,
            CompanyTaxNumber: formData.taxNumber,
            CompanyPanNumber: formData.panNumber,
            CurrencyId: currencies.find(c => c.Code === formData.currency)?.Id || 0,
            CompanyTermCondition: formData.termsAndConditions,
            CustomerNotes: formData.notes,
            QuotationFooter: formData.footer,
            CompanyLogo: formData.logo,
            QuotaionTitle: formData.quotationTitle,
            QuotaionSubHeading: formData.quotationSubHeading,
            QuotationNotes: formData.notes,
            CompanyCountryId: formData.companyCountryId || 0,
            CompanyStateId: formData.companyStateId || 0,
            CompanyCityId: formData.companyCityId || 0,
            CompanyPincode: formData.companyPincode || '',
            CompanyOriginId: 0,
            CompanyOriginName: formData.placeOfOrigin,
            CompanySupplyName: formData.placeOfSupply,

            BillToName: formData.billTo.name,
            BillToType: 'Lead',
            BillToTaxNumber: '',
            BillToPanNumber: '',
            BillToEmailId: formData.billTo.email,
            BillToPhoneNumber: formData.billTo.phone,
            BillToAddress: formData.billTo.address,
            BillToCountryId: formData.billTo.countryId || 0,
            BillToStateId: formData.billTo.stateId || 0,
            BillToCityId: formData.billTo.cityId || 0,
            BillToPincode: formData.billTo.pincode,

            ShipToName: formData.shipTo.name,
            ShipToEmailId: formData.shipTo.email,
            ShipToPhone: formData.shipTo.phone,
            ShipToAddress: formData.shipTo.address,
            ShipToCountryId: formData.shipTo.countryId || 0,
            ShipToStateId: formData.shipTo.stateId || 0,
            ShipToCityId: formData.shipTo.cityId || 0,
            ShipToPincode: formData.shipTo.pincode,

            QuotationNumber: formData.quotationNumber,
            SalesOwner: 0,
            QuotationDate: formData.quotationDate,
            QuotationDueDate: formData.validTillDate,
            QuotationSubTotal: formData.subTotal,
            QuotationTotal: formData.grandTotal,

            Items: formData.items.map(item => ({
                ItemName: item.name,
                ItemId: item.productId || 0,
                ItemSKU: item.sku || '',
                ItemDescription: item.description,
                ItemVolume: item.quantity,
                ItemPrice: item.price,
                ItemAmount: item.amount,
                ItemTaxCharges: item.isTaxEnabled ? [
                    {
                        TCName: 'Tax',
                        TCId: 0,
                        TCRate: item.taxPercent,
                        TCType: 'P',
                        TCAmount: item.taxAmount
                    }
                ] : []
            })),
            AdditionalItems: []
        };
    };

    useImperativeHandle(ref, () => ({
        getFormData: () => formData,
        isValid: validateForm,
        getQuotationData: getQuotationData
    }));

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData({ ...formData, logo: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };



    const fetchItemSuggestions = async (val) => {
        setItemSearchText(val);
        if (val.length > 2) {
            setIsLoadingItems(true);
            try {
                const list = await getProductForSuggetion({ FilterText: val, UserId: userId, SectionName: 'invoice' });
                const apiResults = Array.isArray(list) ? list : [];
                // Always merge in any recently-added products that match the search text
                const lowerVal = val.toLowerCase();
                const recentMatches = recentlyAddedProductsRef.current.filter(p =>
                    (p.Name || '').toLowerCase().includes(lowerVal)
                );
                const existingIds = new Set(apiResults.map(p => p.Id));
                const merged = [
                    ...recentMatches.filter(p => !existingIds.has(p.Id)),
                    ...apiResults
                ];
                setItemSuggestions(merged);
            } catch (err) {
                console.warn('fetchItemSuggestions error', err);
            } finally {
                setIsLoadingItems(false);
            }
        } else {
            setItemSuggestions([]);
        }
    };

    const handleSelectItem = (prod) => {
        let hsnCode = '';
        let isTaxable = false;
        let productDetails = {};
        try {
            if (prod.ProductJSON) {
                productDetails = JSON.parse(prod.ProductJSON);
                hsnCode = productDetails.HSNCode || productDetails.SACCode || '';
                isTaxable = productDetails.IsTaxable === true || productDetails.IsTaxable === 1;
            }
        } catch (e) {
            console.warn('Parse ProductJSON error', e);
        }

        if (prod.IsVariant === 1) {
            let vCombination = [];
            try {
                vCombination = typeof prod.VCombinationJson === 'string'
                    ? JSON.parse(prod.VCombinationJson)
                    : (prod.VCombinationJson?.$values || prod.VCombinationJson || []);
                if (vCombination.$values) vCombination = vCombination.$values;
            } catch (e) {
                console.warn('Parse VCombinationJson error', e);
            }

            setSelectedProductForVariant({
                ...prod,
                productId: prod.Id || prod.ProductId || 0,
                sku: prod.SKU || '',
                ProductName: prod.Name || '',
                SalesPrice: prod.SalePrice || 0,
                HSNCode: hsnCode,
                IsTaxable: isTaxable,
                combinations: vCombination
            });
            setSelectedVariants([]);
            setIsVariantModalOpen(true);
        } else {
            const newItem = {
                id: `item_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                productId: prod.Id || prod.ProductId || 0,
                sku: prod.SKU || '',
                name: prod.Name || '',
                description: '',
                quantity: 1,
                price: prod.SalePrice || 0,
                amount: prod.SalePrice || 0,
                hsnCode: hsnCode,
                taxPercent: 0,
                taxAmount: 0,
                discountPercent: 0,
                discountAmount: 0,
                isTaxEnabled: isTaxable,
                isDiscountEnabled: false,
                isDiscountPercentage: true,
                isVariant: prod.IsVariant || 0,
                vCombinationJson: prod.VCombinationJson || '',
                productJson: prod.ProductJSON || ''
            };
            setFormData(prev => ({
                ...prev,
                items: [...prev.items, newItem]
            }));
            setShowItemSearch(false);
            setItemSearchText('');
            setItemSuggestions([]);
        }
    };

    const toggleVariantSelection = (variant, idx) => {
        const attrStr = Object.entries(variant)
            .filter(([k]) => !['uniqueId', 'quantity', 'id', 'price', 'Price', 'imageName', 'imagePath', 'imageUploaded'].includes(k))
            .map(([k, v]) => `${k}:${v}`)
            .join('|');
        const uniqueId = `v_${idx}_${attrStr}`;

        const isSelected = selectedVariants.some(v => v.uniqueId === uniqueId);
        if (isSelected) {
            setSelectedVariants(prev => prev.filter(v => v.uniqueId !== uniqueId));
        } else {
            setSelectedVariants(prev => [...prev, { ...variant, uniqueId, quantity: 1, Price: selectedProductForVariant.SalesPrice || 0 }]);
        }
    };

    const updateVariantQuantity = (uniqueId, qty) => {
        const q = parseInt(qty) || 1;
        setSelectedVariants(prev => prev.map(v => v.uniqueId === uniqueId ? { ...v, quantity: q } : v));
    };

    const handleConfirmVariants = () => {
        const newItems = selectedVariants.map(v => ({
            id: `item_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
            productId: selectedProductForVariant.productId || 0,
            sku: selectedProductForVariant.sku || '',
            name: `${selectedProductForVariant.ProductName} (${Object.entries(v)
                .filter(([k]) => !['uniqueId', 'quantity', 'id', 'price', 'Price', 'imageName', 'imagePath', 'imageUploaded'].includes(k))
                .map(([k, v]) => `${k}:${v}`)
                .join(', ')})`,
            description: '',
            quantity: v.quantity || 1,
            price: selectedProductForVariant.SalesPrice || 0,
            amount: (v.quantity || 1) * (selectedProductForVariant.SalesPrice || 0),
            hsnCode: selectedProductForVariant.HSNCode || '',
            taxPercent: 0,
            taxAmount: 0,
            discountPercent: 0,
            discountAmount: 0,
            isTaxEnabled: selectedProductForVariant.IsTaxable,
            isDiscountEnabled: false,
            isDiscountPercentage: true,
            isVariant: 0, // Generated variant items themselves are not parents
            productJson: selectedProductForVariant.ProductJSON || ''
        }));

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, ...newItems]
        }));
        setIsVariantModalOpen(false);
        setShowItemSearch(false);
        setItemSearchText('');
        setItemSuggestions([]);
    };

    const reopenVariantModal = (index) => {
        const item = formData.items[index];
        if (!item || item.isVariant !== 1) return;

        let vCombination = [];
        try {
            vCombination = typeof item.vCombinationJson === 'string'
                ? JSON.parse(item.vCombinationJson)
                : (item.vCombinationJson?.$values || item.vCombinationJson || []);
            if (vCombination.$values) vCombination = vCombination.$values;
        } catch (e) {
            console.warn('reopenVariantModal parse error', e);
        }

        setSelectedProductForVariant({
            productId: item.productId,
            ProductName: item.name,
            SalesPrice: item.price,
            HSNCode: item.hsnCode,
            IsTaxable: item.isTaxEnabled,
            combinations: vCombination,
            ProductJSON: item.productJson
        });
        setSelectedVariants([]);
        setIsVariantModalOpen(true);
    };

    const openAddressModal = async () => {
        const initialData = {
            ...formData.billTo,
            email: getFirstField(formData.billTo.email),
            phone: stripCountryCode(getFirstField(formData.billTo.phone))
        };
        setEditAddressData(initialData);
        setIsAddressModalOpen(true);

        try {
            // Resolve countryId from name if missing
            let resolvedCountryId = initialData.countryId;
            if (!resolvedCountryId && initialData.country) {
                const matched = countries.find(c => (c.Text || c.Name || '').toLowerCase() === initialData.country.toLowerCase());
                if (matched) {
                    resolvedCountryId = matched.Id;
                    setEditAddressData(prev => ({ ...prev, countryId: matched.Id }));
                }
            }
            if (resolvedCountryId) {
                const st = await getStateListByCountryId({ Details: { CountryId: resolvedCountryId } });
                const statesArr = Array.isArray(st) ? st : [];
                setStates(statesArr);

                // Resolve stateId from name if missing
                let resolvedStateId = initialData.stateId;
                if (!resolvedStateId && initialData.state) {
                    const matched = statesArr.find(s => (s.Text || s.Name || '').toLowerCase() === initialData.state.toLowerCase());
                    if (matched) {
                        resolvedStateId = matched.Id;
                        setEditAddressData(prev => ({ ...prev, stateId: matched.Id }));
                    }
                }
                if (resolvedStateId) {
                    const ct = await getCityListByStateId({ Details: { StateId: resolvedStateId } });
                    const citiesArr = Array.isArray(ct) ? ct : [];
                    setCities(citiesArr);

                    // Resolve cityId from name if missing
                    if (!initialData.cityId && initialData.city) {
                        const matched = citiesArr.find(c => (c.Text || c.Name || '').toLowerCase() === initialData.city.toLowerCase());
                        if (matched) setEditAddressData(prev => ({ ...prev, cityId: matched.Id }));
                    }
                }
            }
        } catch (err) {
            console.warn('openAddressModal pre-fetch error', err);
        }
    };

    const handleAddressApply = () => {
        setFormData(prev => {
            const updatedBillTo = {
                ...editAddressData,
                email: getFirstField(editAddressData.email),
                phone: stripCountryCode(editAddressData.phone ? getFirstField(editAddressData.phone) : '')
            };
            return {
                ...prev,
                billTo: updatedBillTo,
                shipTo: prev.sameAsBillTo ? { ...updatedBillTo } : prev.shipTo
            };
        });
        setIsAddressModalOpen(false);
    };

    const openShipToModal = async () => {
        const initialData = {
            ...formData.shipTo,
            email: getFirstField(formData.shipTo.email),
            phone: stripCountryCode(getFirstField(formData.shipTo.phone))
        };
        setEditAddressData(initialData);
        setIsShipToModalOpen(true);

        try {
            // Resolve countryId from name if missing
            let resolvedCountryId = initialData.countryId;
            if (!resolvedCountryId && initialData.country) {
                const matched = countries.find(c => (c.Text || c.Name || '').toLowerCase() === initialData.country.toLowerCase());
                if (matched) {
                    resolvedCountryId = matched.Id;
                    setEditAddressData(prev => ({ ...prev, countryId: matched.Id }));
                }
            }
            if (resolvedCountryId) {
                const st = await getStateListByCountryId({ Details: { CountryId: resolvedCountryId } });
                const statesArr = Array.isArray(st) ? st : [];
                setStates(statesArr);

                // Resolve stateId from name if missing
                let resolvedStateId = initialData.stateId;
                if (!resolvedStateId && initialData.state) {
                    const matched = statesArr.find(s => (s.Text || s.Name || '').toLowerCase() === initialData.state.toLowerCase());
                    if (matched) {
                        resolvedStateId = matched.Id;
                        setEditAddressData(prev => ({ ...prev, stateId: matched.Id }));
                    }
                }
                if (resolvedStateId) {
                    const ct = await getCityListByStateId({ Details: { StateId: resolvedStateId } });
                    const citiesArr = Array.isArray(ct) ? ct : [];
                    setCities(citiesArr);

                    // Resolve cityId from name if missing
                    if (!initialData.cityId && initialData.city) {
                        const matched = citiesArr.find(c => (c.Text || c.Name || '').toLowerCase() === initialData.city.toLowerCase());
                        if (matched) setEditAddressData(prev => ({ ...prev, cityId: matched.Id }));
                    }
                }
            }
        } catch (err) {
            console.warn('openShipToModal pre-fetch error', err);
        }
    };

    const handleShipToApply = () => {
        setFormData(prev => ({
            ...prev,
            shipTo: {
                ...editAddressData,
                email: getFirstField(editAddressData.email),
                phone: stripCountryCode(editAddressData.phone ? getFirstField(editAddressData.phone) : '')
            },
            sameAsBillTo: false
        }));
        setIsShipToModalOpen(false);
    };

    const openDescEditModal = (index) => {
        setEditingDescIndex(index);
        setDescTempText(formData.items[index].description || '');
        setIsDescEditModalOpen(true);
    };

    const handleApplyDesc = () => {
        updateItem(editingDescIndex, 'description', descTempText);
        setIsDescEditModalOpen(false);
    };

    const applyFormatting = (tag) => {
        const textarea = document.getElementById("txtItemDescription");
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = textarea.value.substring(start, end);
        let replacement = '';
        switch (tag) {
            case 'B': replacement = `*${selectedText}*`; break;
            case 'I': replacement = `--${selectedText}--`; break;
            case 'U': replacement = `_${selectedText}_`; break;
            case 'S': replacement = `~${selectedText}~`; break;
            case 'C': replacement = `\`\`\`${selectedText}\`\`\``; break;
            default: break;
        }
        const newValue = textarea.value.substring(0, start) + replacement + textarea.value.substring(end);
        setDescTempText(newValue);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start, start + replacement.length);
        }, 0);
    };

    const insertPlaceholder = (val) => {
        if (!val) return;
        const textarea = document.getElementById("txtItemDescription");
        if (!textarea) return;
        const pos = textarea.selectionStart;
        const text = textarea.value;
        const placeholder = `#${val}#`;
        const newValue = text.substring(0, pos) + placeholder + text.substring(pos);
        setDescTempText(newValue);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(pos + placeholder.length, pos + placeholder.length);
        }, 0);
    };

    const fetchCustomerSuggestions = async (val) => {
        setCustomerSearchText(val);
        if (val.length > 2) {
            setIsLoadingCustomers(true);
            try {
                const list = await getLeadCustomerSuggection({ Details: { UserId: userId, FilterText: val } });
                setCustomerSuggestions(Array.isArray(list) ? list : []);
            } catch (err) {
                console.warn('fetchCustomerSuggestions error', err);
            } finally {
                setIsLoadingCustomers(false);
            }
        } else {
            setCustomerSuggestions([]);
        }
    };

    const handleSelectCustomer = async (cust) => {
        try {
            const lId = cust.LeadId || cust.Id || cust.ID;
            const lResp = await getLeadSuggectionById({ LeadId: lId, UserId: userId });
            if (lResp) {
                const lData = Array.isArray(lResp) ? lResp[0] : lResp;
                if (lData) {
                    const updatedBillTo = {
                        name: lData.PersonName || lData.Name || '',
                        phone: stripCountryCode(getFirstField(lData.MobileNo || lData.Phone || '')),
                        email: getFirstField(lData.EmailID || lData.Email || ''),
                        address: lData.Address || '',
                        city: lData.City || '',
                        state: lData.State || '',
                        country: lData.Country || '',
                        pincode: lData.PinCode || '',
                        taxNumber: lData.TaxNumber || '',
                        panNumber: lData.PanNumber || ''
                    };

                    setFormData(prev => ({
                        ...prev,
                        billTo: updatedBillTo,
                        shipTo: prev.sameAsBillTo ? { ...updatedBillTo } : prev.shipTo
                    }));
                }
            }
        } catch (err) {
            console.warn('handleSelectCustomer error', err);
        } finally {
            setShowCustomerSearch(false);
            setCustomerSearchText('');
            setCustomerSuggestions([]);
        }
    };


    return (
        <div className="space-y-6 p-1">
            <div className="border rounded-lg overflow-hidden shadow-sm bg-white">
                <div
                    className="bg-[#2a9629] text-white px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-[#238022] transition-colors"
                    onClick={() => setIsCompanyOpen(!isCompanyOpen)}
                >
                    <div className="flex items-center gap-2">
                        <div className="p-1 bg-white/20 rounded">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M7 7l5 5-5 5M13 7l5 5-5 5" />
                            </svg>
                        </div>
                        <span className="font-semibold text-sm">Company</span>
                    </div>
                    {isCompanyOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>

                {isCompanyOpen && (
                    <div className="p-6 space-y-6">
                        <div className="flex flex-col md:flex-row gap-8">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-40 h-24 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50 group relative">
                                    {formData.logo ? (
                                        <img src={formData.logo} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                                    ) : (
                                        <div className="text-gray-400 text-xs text-center p-2">
                                            <Plus className="w-6 h-6 mx-auto mb-1 opacity-50" />
                                            Your logo
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                        onChange={handleLogoChange}
                                        accept="image/*"
                                    />
                                </div>
                                {formData.logo && (
                                    <button
                                        onClick={() => setFormData({ ...formData, logo: '' })}
                                        className="text-xs px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                    >
                                        remove
                                    </button>
                                )}
                            </div>

                            <div className="flex-1 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Title</label>
                                        <input
                                            type="text"
                                            value={formData.quotationTitle}
                                            onChange={(e) => setFormData({ ...formData, quotationTitle: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                                            placeholder="Quotation"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Subheading</label>
                                        <input
                                            type="text"
                                            value={formData.quotationSubHeading}
                                            onChange={(e) => setFormData({ ...formData, quotationSubHeading: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                                            placeholder="This is your Quotation"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Place of Supply</label>
                                        <input
                                            type="text"
                                            value={formData.placeOfSupply}
                                            onChange={(e) => setFormData({ ...formData, placeOfSupply: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                                            placeholder="Enter Place of Supply"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Place of Origin</label>
                                        <input
                                            type="text"
                                            value={formData.placeOfOrigin}
                                            onChange={(e) => setFormData({ ...formData, placeOfOrigin: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                                            placeholder="Enter Place of Origin"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row justify-between items-start gap-6 pt-4">
                            <div className="space-y-2 text-sm text-gray-600 max-w-md">
                                <div className="text-gray-500 text-xs font-medium">{formData.companyName}</div>
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-gray-900" />
                                    <span className="text-gray-900 font-medium">{formData.companyEmail}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-gray-900" />
                                    <span className="text-gray-900 font-medium">{formData.companyPhone}</span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-gray-900 mt-0.5" />
                                    <span className="flex-1 leading-relaxed text-gray-900 font-medium">{formData.companyAddress}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Percent className="w-4 h-4 text-gray-900" />
                                    <span className="text-gray-900 font-medium">Tax Number: {formData.taxNumber}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Percent className="w-4 h-4 text-gray-900" />
                                    <span className="text-gray-900 font-medium">Pan Number: {formData.panNumber}</span>
                                </div>
                            </div>
                            <button className="text-[#00aaff] text-sm font-bold hover:underline">
                                Edit your business address
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 relative group">
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="text-sm font-bold text-gray-800">Bill To</h4>
                        <button
                            onClick={openAddressModal}
                            className="p-1.5 text-blue-600 bg-white rounded-full shadow-sm hover:bg-blue-50 transition border border-blue-100"
                        >
                            <Edit className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <div className="text-sm space-y-1.5 text-gray-700">
                        <p className="font-bold text-gray-900 text-base">{formData.billTo.name || '---'}</p>
                        {formData.billTo.taxNumber && <p className="text-gray-600 font-medium">Tax Number: {formData.billTo.taxNumber}</p>}
                        {formData.billTo.email && <p className="text-gray-600 break-all">{formData.billTo.email}</p>}
                        {formData.billTo.phone && <p className="text-gray-600">{formData.billTo.phone}</p>}
                        <p className="text-gray-600 leading-relaxed">
                            {formData.billTo.address && `${formData.billTo.address}, `}
                            {formData.billTo.city && `${formData.billTo.city}, `}
                            {formData.billTo.state && `${formData.billTo.state}, `}
                            {formData.billTo.country && `${formData.billTo.country}, `}
                            {formData.billTo.pincode}
                        </p>
                        {showCustomerSearch ? (
                            <div className="mt-2 relative">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        autoFocus
                                        placeholder="Search customer..."
                                        value={customerSearchText}
                                        onChange={(e) => fetchCustomerSuggestions(e.target.value)}
                                        className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 outline-none"
                                    />
                                    <button
                                        onClick={() => { setShowCustomerSearch(false); setCustomerSearchText(''); setCustomerSuggestions([]); }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <Plus className="w-4 h-4 rotate-45" />
                                    </button>
                                </div>
                                {customerSuggestions.length > 0 && (
                                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border rounded shadow-lg max-h-40 overflow-y-auto">
                                        {customerSuggestions.map((cust) => (
                                            <div
                                                key={cust.LeadId || cust.Id}
                                                onClick={() => handleSelectCustomer(cust)}
                                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-0 text-xs"
                                            >
                                                <div className="font-bold text-gray-900">{cust.PersonName}</div>
                                                <div className="text-[10px] text-gray-500">{cust.MobileNo} {cust.EmailID && `| ${cust.EmailID}`}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {isLoadingCustomers && (
                                    <div className="absolute right-8 top-1.5">
                                        <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowCustomerSearch(true)}
                                className="text-blue-500 text-xs font-medium hover:underline mt-2 inline-block"
                            >
                                Choose a different customer
                            </button>
                        )}
                    </div>
                </div>

                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 relative group">
                    <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-gray-800">Ship To</h4>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.sameAsBillTo}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData({
                                            ...formData,
                                            sameAsBillTo: checked,
                                            shipTo: checked ? { ...formData.billTo } : formData.shipTo
                                        });
                                    }}
                                    className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-blue-500 border-gray-300 transition"
                                />
                                <span className="text-[10px] text-gray-500 font-medium">Same as Bill To</span>
                            </label>
                        </div>
                        <button
                            onClick={openShipToModal}
                            className="p-1.5 text-blue-600 bg-white rounded-full shadow-sm hover:bg-blue-50 transition border border-blue-100"
                        >
                            <Edit className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <div className="text-sm space-y-1.5 text-gray-700">
                        <p className="font-bold text-gray-900 text-base">{formData.shipTo.name || '---'}</p>
                        {formData.shipTo.email && <p className="text-gray-600 break-all">{formData.shipTo.email}</p>}
                        {formData.shipTo.phone && <p className="text-gray-600">{formData.shipTo.phone}</p>}
                        <p className="text-gray-600 leading-relaxed">
                            {formData.shipTo.address && `${formData.shipTo.address}, `}
                            {formData.shipTo.city && `${formData.shipTo.city}, `}
                            {formData.shipTo.state && `${formData.shipTo.state}, `}
                            {formData.shipTo.country && `${formData.shipTo.country}, `}
                            {formData.shipTo.pincode}
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Quotation Number</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FileText className="w-4 h-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={formData.quotationNumber}
                                    onChange={(e) => setFormData({ ...formData, quotationNumber: e.target.value })}
                                    className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                                    placeholder="QUO-001"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Currency</label>
                            <select
                                value={formData.currency}
                                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                            >
                                {currencies.map((curr) => (
                                    <option key={curr.Id} value={curr.Code}>
                                        {curr.Symbol} {curr.Code}
                                    </option>
                                ))}
                                {currencies.length === 0 && <option value="INR">₹ INR</option>}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Quotation Date</label>
                            <input
                                type="date"
                                value={formData.quotationDate}
                                onChange={(e) => setFormData({ ...formData, quotationDate: e.target.value })}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Valid Till Date</label>
                            <input
                                type="date"
                                value={formData.validTillDate}
                                onChange={(e) => setFormData({ ...formData, validTillDate: e.target.value })}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="border rounded-lg overflow-visible bg-white shadow-sm">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr className="text-left text-[10px] font-bold text-gray-500 uppercase tracking-tight">
                            <th className="px-4 py-3 w-8"></th>
                            <th className="px-2 py-3 min-w-[200px]">Items</th>
                            <th className="px-2 py-3 w-32">Preview</th>
                            <th className="px-2 py-3 text-center w-16">Qty</th>
                            <th className="px-2 py-3 text-right w-28">Price</th>
                            <th className="px-2 py-3 text-right w-28">Amount</th>
                            <th className="px-2 py-3 w-24">HSN/SAC</th>
                            <th className="px-2 py-3 w-20">Taxes</th>
                            <th className="px-2 py-3 text-right w-24">Tax Amt</th>
                            <th className="px-4 py-3 w-10"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {formData.items.map((item, index) => (
                            <React.Fragment key={item.id}>
                                <tr className="group hover:bg-gray-50/50 transition-colors">
                                    <td className="px-4 py-4 align-top">
                                        <div className="text-gray-300 group-hover:text-gray-400 cursor-move pt-1">
                                            <svg width="12" height="18" viewBox="0 0 12 18">
                                                <circle cx="2" cy="2" r="1.5" fill="currentColor" />
                                                <circle cx="2" cy="8" r="1.5" fill="currentColor" />
                                                <circle cx="2" cy="14" r="1.5" fill="currentColor" />
                                                <circle cx="10" cy="2" r="1.5" fill="currentColor" />
                                                <circle cx="10" cy="8" r="1.5" fill="currentColor" />
                                                <circle cx="10" cy="14" r="1.5" fill="currentColor" />
                                            </svg>
                                        </div>
                                    </td>
                                    <td className="px-2 py-4 align-top min-w-[200px]">
                                        <div className={`space-y-1 relative ${activeItemIndex === index ? 'z-30' : 'z-0'}`}>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={item.name}
                                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                    onFocus={() => setActiveItemIndex(index)}
                                                    placeholder="Item name"
                                                    className="w-full font-medium text-gray-900 border-none bg-transparent p-0 focus:ring-0 placeholder-gray-400"
                                                />
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                                                    <button className="p-1 text-gray-400 hover:text-blue-500"><Edit className="w-3 h-3" /></button>
                                                    {item.isVariant === 1 && (
                                                        <button
                                                            onClick={() => reopenVariantModal(index)}
                                                            className="p-1 text-gray-400 hover:text-blue-500"
                                                            title="Add Variants"
                                                        >
                                                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <textarea
                                                value={item.description}
                                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                                placeholder="Description (optional)"
                                                rows={1}
                                                className="w-full text-xs text-gray-500 border-none bg-transparent p-0 focus:ring-0 resize-none overflow-hidden placeholder-gray-300"
                                            />
                                            {activeItemIndex === index && productSuggestions.length > 0 && (
                                                <div ref={suggestionRef} className="absolute z-30 left-0 top-full mt-1 w-full bg-white border border-blue-200 rounded-lg shadow-2xl max-h-60 overflow-y-auto">
                                                    {productSuggestions.map((prod) => (
                                                        <div
                                                            key={prod.Id}
                                                            onClick={() => selectProduct(index, prod)}
                                                            className="px-4 py-2 border-l-4 border-l-transparent hover:border-l-blue-500 hover:bg-blue-50/80 cursor-pointer border-b last:border-0 transition-all"
                                                        >
                                                            <div className="font-medium text-sm text-gray-900">{prod.ProductName}</div>
                                                            <div className="text-[10px] text-gray-500 flex gap-3 items-center">
                                                                <span>Price: ₹{prod.SalesPrice}</span>
                                                                {prod.HSNCode && <span>HSN: {prod.HSNCode}</span>}
                                                                {prod.IsVariant === 1 && <span className="text-blue-600 font-bold uppercase tracking-tighter text-[8px] bg-blue-50 px-1 rounded">Has Variants</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-2 py-4 align-top w-32">
                                        <div className="flex items-start justify-between gap-2">
                                            <div
                                                className="text-[11px] text-gray-600 leading-relaxed max-h-12 overflow-hidden"
                                                dangerouslySetInnerHTML={{ __html: itemPreviewHandler(item.description) }}
                                            />
                                            <button
                                                onClick={() => openDescEditModal(index)}
                                                className="p-1 text-gray-400 hover:text-blue-500 transition-colors shrink-0 mt-0.5"
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-2 py-4 align-top w-16">
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                            className="w-full text-center border-none bg-transparent p-0 focus:ring-0 font-medium"
                                        />
                                    </td>
                                    <td className="px-2 py-4 align-top w-28 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <span className="text-gray-400 text-xs">₹</span>
                                            <input
                                                type="number"
                                                value={item.price}
                                                onChange={(e) => updateItem(index, 'price', e.target.value)}
                                                className="w-20 text-right border-none bg-transparent p-0 focus:ring-0 font-medium"
                                            />
                                        </div>
                                    </td>
                                    <td className="px-2 py-4 align-top w-28 text-right">
                                        <span className="font-bold text-gray-900">₹ {item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </td>
                                    <td className="px-2 py-4 align-top w-24 text-gray-600 text-[10px]">
                                        HSN: {item.hsnCode || '---'}
                                    </td>
                                    <td className="px-2 py-4 align-top w-20">
                                        {item.isTaxEnabled ? (
                                            <div className={`flex flex-col gap-1 relative ${activeTaxSearchIndex === index ? 'z-[70]' : 'z-0'}`}>
                                                <div
                                                    className="text-[10px] text-blue-600 font-bold cursor-pointer hover:underline truncate max-w-[80px]"
                                                    onClick={() => setActiveTaxSearchIndex(index)}
                                                >
                                                    {item.taxName || 'Select Tax'}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        value={item.taxPercent}
                                                        onChange={(e) => updateItem(index, 'taxPercent', e.target.value)}
                                                        className="w-12 text-xs border border-gray-200 rounded px-1 py-0.5"
                                                    />
                                                    <span className="text-[10px] text-gray-400">%</span>
                                                </div>

                                                {activeTaxSearchIndex === index && (
                                                    <div className="absolute z-[70] left-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                                                        <div className="p-2 border-b">
                                                            <input
                                                                type="text"
                                                                autoFocus
                                                                placeholder="Search Tax..."
                                                                value={taxSearchText}
                                                                onChange={(e) => handleTaxSearch(index, e.target.value)}
                                                                className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                        <div className="max-h-40 overflow-y-auto">
                                                            {isLoadingTaxes ? (
                                                                <div className="p-2 text-center text-[10px] text-gray-400">Loading...</div>
                                                            ) : taxSuggestions.length > 0 ? (
                                                                taxSuggestions.map((tax) => (
                                                                    <div
                                                                        key={tax.Id}
                                                                        onClick={() => handleSelectTax(index, tax)}
                                                                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-0 flex justify-between items-center"
                                                                    >
                                                                        <span className="text-xs font-medium text-gray-700">{tax.Name}</span>
                                                                        <span className="text-[10px] text-gray-500 font-bold">{tax.Rate}%</span>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="p-2 text-center text-[10px] text-gray-400">No results found</div>
                                                            )}
                                                            <div className="p-2 text-[10px] text-blue-600 font-bold border-t cursor-pointer hover:bg-blue-50 flex items-center gap-1">
                                                                <Plus className="w-2.5 h-2.5" /> Add New Tax
                                                            </div>
                                                        </div>
                                                        <div className="p-1 bg-gray-50 border-t text-right">
                                                            <button
                                                                onClick={() => setActiveTaxSearchIndex(null)}
                                                                className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-0.5"
                                                            >
                                                                Close
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : '---'}
                                    </td>
                                    <td className="px-2 py-4 align-top w-24 text-right">
                                        <span className="text-gray-600 text-[10px]">₹ {item.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </td>
                                    <td className="px-4 py-4 align-top">
                                        <button
                                            onClick={() => removeItem(index)}
                                            className="p-1.5 text-gray-300 hover:text-red-500 transition"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan="10" className="px-4 pb-4">
                                        <div className="flex flex-col gap-2">
                                            {item.isDiscountEnabled && (
                                                <div className="flex items-center justify-end gap-4 bg-gray-50/50 p-2 rounded-lg border border-gray-100 w-fit ml-auto mr-14">
                                                    <span className="text-xs font-medium text-gray-500">Discount</span>
                                                    <div className="flex items-center bg-white border border-gray-200 rounded-md p-1 shadow-sm">
                                                        <button
                                                            onClick={() => updateItem(index, 'isDiscountPercentage', true)}
                                                            className={`px-2 py-0.5 text-[10px] rounded transition ${item.isDiscountPercentage ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-100'}`}
                                                        >
                                                            %
                                                        </button>
                                                        <button
                                                            onClick={() => updateItem(index, 'isDiscountPercentage', false)}
                                                            className={`px-2 py-0.5 text-[10px] rounded transition ${!item.isDiscountPercentage ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-100'}`}
                                                        >
                                                            ₹
                                                        </button>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        value={item.isDiscountPercentage ? item.discountPercent : item.discountAmount}
                                                        onChange={(e) => updateItem(index, item.isDiscountPercentage ? 'discountPercent' : 'discountAmount', e.target.value)}
                                                        className="w-16 text-right text-xs font-bold border-b border-gray-300 bg-transparent focus:border-blue-500 focus:ring-0 p-0"
                                                    />
                                                    <span className="text-xs font-bold text-gray-900 min-w-[60px] text-right">
                                                        ₹ {(item.isDiscountPercentage ? ((item.quantity * item.price) * (item.discountPercent / 100)) : item.discountAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                    </span>
                                                    <button onClick={() => updateItem(index, 'isDiscountEnabled', false)} className="text-gray-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                                                </div>
                                            )}
                                            <div className="flex justify-end gap-4 pr-16">
                                                {!item.isDiscountEnabled && (
                                                    <button
                                                        onClick={() => updateItem(index, 'isDiscountEnabled', true)}
                                                        className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-700 transition bg-blue-50 px-2 py-1 rounded"
                                                    >
                                                        <Plus className="w-3 h-3" /> Add Discount
                                                    </button>
                                                )}
                                                {!item.isTaxEnabled && (
                                                    <button
                                                        onClick={() => {
                                                            updateItem(index, 'isTaxEnabled', true);
                                                            setActiveTaxSearchIndex(index);
                                                        }}
                                                        className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 hover:text-indigo-700 transition bg-indigo-50 px-2 py-1 rounded"
                                                    >
                                                        <Plus className="w-3 h-3" /> Add Tax
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                    {!showItemSearch ? (
                        <button
                            onClick={() => setShowItemSearch(true)}
                            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-bold text-sm transition"
                        >
                            <Plus className="w-4 h-4" />
                            Add New Item
                        </button>
                    ) : (
                        <div className="max-w-2xl relative">
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Type an item name to search..."
                                            value={itemSearchText}
                                            onChange={(e) => fetchItemSuggestions(e.target.value)}
                                            className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none shadow-sm transition-all"
                                        />
                                        {itemSearchText && (
                                            <button
                                                onClick={() => { setItemSearchText(''); setItemSuggestions([]); }}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setIsAddProductOpen(true)}
                                        className="px-4 py-2.5 bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-bold flex items-center gap-2 transition-all shadow-sm whitespace-nowrap"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add New Product
                                    </button>
                                    <button
                                        onClick={() => fetchItemSuggestions(itemSearchText)}
                                        disabled={!itemSearchText || isLoadingItems}
                                        className="p-2.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 border border-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        title="Reload product list"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${isLoadingItems ? 'animate-spin' : ''}`} />
                                    </button>
                                    <button
                                        onClick={() => { setShowItemSearch(false); setItemSearchText(''); setItemSuggestions([]); }}
                                        className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                        title="Close search"
                                    >
                                        <Plus className="w-5 h-5 rotate-45" />
                                    </button>
                                </div>
                                  {(itemSuggestions.length > 0 || (itemSearchText.length > 0 && !isLoadingItems)) && (
                                <div className="absolute z-[60] left-0 right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-black/5">
                                    <div className="max-h-[300px] overflow-y-auto">
                                        {itemSuggestions.length > 0 ? (
                                            itemSuggestions.map((prod) => (
                                                <div
                                                    key={prod.Id}
                                                    onClick={() => handleSelectItem(prod)}
                                                    className="px-4 py-3 border-l-4 border-l-transparent hover:border-l-blue-500 hover:bg-blue-50/80 cursor-pointer border-b border-gray-50 last:border-0 transition-all flex items-center justify-between group"
                                                >
                                                    <div className="space-y-0.5">
                                                        <div className="font-bold text-sm text-gray-900 group-hover:text-blue-700">{prod.Name}</div>
                                                        <div className="text-[10px] text-gray-500 flex items-center gap-3">
                                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded font-medium text-gray-600">Price: ₹{prod.SalePrice}</span>
                                                            {prod.Sku && <span>SKU: {prod.Sku}</span>}
                                                            {prod.IsVariant === 1 && <span className="text-blue-600 font-bold uppercase tracking-tighter text-[8px]">Has Variants</span>}
                                                        </div>
                                                    </div>
                                                    <Plus className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-6 py-8 text-center bg-gray-50/50">
                                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                    <Search className="w-6 h-6 text-gray-400" />
                                                </div>
                                                <p className="text-sm font-medium text-gray-600">No products found matching "{itemSearchText}"</p>
                                                <p className="text-xs text-gray-400 mt-1">Try a different name or add it as a new product below.</p>
                                            </div>
                                        )}
                                        <div 
                                            onClick={() => setIsAddProductOpen(true)}
                                            className="px-4 py-3.5 bg-blue-50 hover:bg-blue-100 cursor-pointer transition-colors flex items-center gap-3 group border-t border-blue-100"
                                        >
                                            <div className="p-2 bg-blue-500 rounded-full text-white shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform">
                                                <Plus className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold text-blue-700">Add "{itemSearchText}" as a New Product</div>
                                                <div className="text-[10px] text-blue-500 mt-0.5 font-medium">Create product profile to use it in future transactions.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isLoadingItems && (
                                <div className="absolute right-12 top-1/2 -translate-y-1/2">
                                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end">
                <div className="w-full md:w-80 bg-white p-6 rounded-lg border shadow-sm space-y-3">
                    <div className="flex justify-between text-sm text-gray-600">
                        <span>Subtotal</span>
                        <span className="font-medium text-gray-900">{formData.subTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                        <span>Total Tax</span>
                        <span className="font-medium text-blue-600">+{formData.totalTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                        <span className="font-semibold text-gray-900">Grand Total</span>
                        <span className="font-bold text-lg text-gray-900">
                            {formData.currency === 'INR' ? '₹' : formData.currency} {formData.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <span className="font-bold text-sm text-gray-700">Terms & Condition</span>
                    </div>
                    <div className="p-4">
                        <textarea
                            id="termsEditorQ"
                            defaultValue={formData.termsAndConditions || ''}
                            className="w-full text-sm text-gray-700 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y min-h-[100px] p-3"
                            placeholder="Enter terms and conditions..."
                        />
                    </div>
                </div>

                <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <span className="font-bold text-sm text-gray-700">Notes</span>
                    </div>
                    <div className="p-4">
                        <textarea
                            id="notesEditorQ"
                            defaultValue={formData.notes || ''}
                            className="w-full text-sm text-gray-700 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y min-h-[100px] p-3"
                            placeholder="Enter notes..."
                        />
                    </div>
                </div>

                <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                    <div
                        className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between cursor-pointer"
                        onClick={() => setIsFooterOpen(!isFooterOpen)}
                    >
                        <span className="font-bold text-sm text-gray-700">Footer</span>
                        {isFooterOpen ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                    {isFooterOpen && (
                        <div className="p-4">
                            <textarea
                                id="footerEditorQ"
                                defaultValue={formData.footer || ''}
                                className="w-full text-sm text-gray-700 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y min-h-[100px] p-3"
                                placeholder="Enter footer details..."
                            />
                        </div>
                    )}
                </div>
            </div>

            {isAddressModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Edit your lead address</h3>
                            <button onClick={() => setIsAddressModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <Plus className="w-5 h-5 rotate-45" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto max-h-[80vh]">
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Lead Tax</label>
                                <select className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm">
                                    <option>Nothing selected</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Name</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={editAddressData.name}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, name: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Tax Number</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Percent className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={editAddressData.taxNumber || ''}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, taxNumber: e.target.value })}
                                            placeholder="Enter Tax Number"
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Email</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            value={editAddressData.email || ''}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, email: e.target.value })}
                                            placeholder="Enter Email"
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Phone</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={editAddressData.phone}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, phone: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Country</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <MapPin className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <select
                                        value={editAddressData.countryId || ''}
                                        onChange={handleModalCountryChange}
                                        className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm appearance-none"
                                    >
                                        <option value="">--- Select Country ---</option>
                                        {countries.map(c => (
                                            <option key={c.Id} value={c.Id}>{c.Text || c.Name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">State</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <select
                                            value={editAddressData.stateId || ''}
                                            onChange={handleModalStateChange}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm appearance-none"
                                            disabled={!editAddressData.countryId}
                                        >
                                            <option value="">--- Select State ---</option>
                                            {states.map(s => (
                                                <option key={s.Id} value={s.Id}>{s.Text || s.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">City</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <select
                                            value={editAddressData.cityId || ''}
                                            onChange={(e) => {
                                                const cityName = e.target.options[e.target.selectedIndex].text;
                                                setEditAddressData(prev => ({ ...prev, cityId: e.target.value, city: cityName }));
                                            }}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm appearance-none"
                                            disabled={!editAddressData.stateId}
                                        >
                                            <option value="">--- Select City ---</option>
                                            {cities.map(c => (
                                                <option key={c.Id} value={c.Id}>{c.Text || c.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Pincode</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={editAddressData.pincode}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, pincode: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Pan Number</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Percent className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={editAddressData.panNumber || ''}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, panNumber: e.target.value })}
                                            placeholder="Enter Pan Number"
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Address</label>
                                <textarea
                                    rows={3}
                                    value={editAddressData.address}
                                    onChange={(e) => setEditAddressData({ ...editAddressData, address: e.target.value })}
                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                    placeholder="Enter Address"
                                />
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                onClick={() => setIsAddressModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleAddressApply}
                                className="px-6 py-2 text-sm font-bold text-white bg-[#2a9629] hover:bg-[#238022] rounded-md shadow-sm transition"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isShipToModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Edit ShipTo</h3>
                            <button onClick={() => setIsShipToModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <Plus className="w-5 h-5 rotate-45" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto max-h-[80vh]">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Name</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={editAddressData.name}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, name: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Email</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            value={editAddressData.email || ''}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, email: e.target.value })}
                                            placeholder="Enter Email"
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Phone</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={editAddressData.phone}
                                            onChange={(e) => setEditAddressData({ ...editAddressData, phone: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Country</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <select
                                            value={editAddressData.countryId || ''}
                                            onChange={handleModalCountryChange}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm appearance-none"
                                        >
                                            <option value="">--- Select Country ---</option>
                                            {countries.map(c => (
                                                <option key={c.Id} value={c.Id}>{c.Text || c.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">State</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <select
                                            value={editAddressData.stateId || ''}
                                            onChange={handleModalStateChange}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm appearance-none"
                                            disabled={!editAddressData.countryId}
                                        >
                                            <option value="">--- Select State ---</option>
                                            {states.map(s => (
                                                <option key={s.Id} value={s.Id}>{s.Text || s.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">City</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                        </div>
                                        <select
                                            value={editAddressData.cityId || ''}
                                            onChange={(e) => {
                                                const cityName = e.target.options[e.target.selectedIndex].text;
                                                setEditAddressData(prev => ({ ...prev, cityId: e.target.value, city: cityName }));
                                            }}
                                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm appearance-none"
                                            disabled={!editAddressData.stateId}
                                        >
                                            <option value="">--- Select City ---</option>
                                            {cities.map(c => (
                                                <option key={c.Id} value={c.Id}>{c.Text || c.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Pincode</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <MapPin className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={editAddressData.pincode}
                                        onChange={(e) => setEditAddressData({ ...editAddressData, pincode: e.target.value })}
                                        className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-tight">Address</label>
                                <textarea
                                    rows={3}
                                    value={editAddressData.address}
                                    onChange={(e) => setEditAddressData({ ...editAddressData, address: e.target.value })}
                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
                                    placeholder="Enter Address"
                                />
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                onClick={() => setIsShipToModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleShipToApply}
                                className="px-6 py-2 text-sm font-bold text-white bg-[#2a9629] hover:bg-[#238022] rounded-md shadow-sm transition"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isVariantModalOpen && selectedProductForVariant && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Select Variant</h3>
                            <button onClick={() => setIsVariantModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <Plus className="w-5 h-5 rotate-45" />
                            </button>
                        </div>

                        <div className="p-6 overflow-x-auto max-h-[60vh]">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-3 py-3 text-left text-xs font-bold text-blue-900 uppercase">Select</th>
                                        {Object.keys(selectedProductForVariant.combinations[0] || {})
                                            .filter(k => !['imageName', 'imagePath', 'imageUploaded', 'id'].includes(k))
                                            .map(key => (
                                                <th key={key} className="px-3 py-3 text-left text-xs font-bold text-blue-900 uppercase">{key}</th>
                                            ))
                                        }
                                        <th className="px-3 py-3 text-left text-xs font-bold text-blue-900 uppercase">Price</th>
                                        <th className="px-3 py-3 text-left text-xs font-bold text-blue-900 uppercase">Quantity</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {selectedProductForVariant.combinations.map((variant, idx) => {
                                        const attrStr = Object.entries(variant)
                                            .filter(([k]) => !['uniqueId', 'quantity', 'id', 'price', 'Price', 'imageName', 'imagePath', 'imageUploaded'].includes(k))
                                            .map(([k, v]) => `${k}:${v}`)
                                            .join('|');
                                        const uniqueId = `v_${idx}_${attrStr}`;
                                        const isSelected = selectedVariants.some(v => v.uniqueId === uniqueId);
                                        const currentV = selectedVariants.find(v => v.uniqueId === uniqueId);

                                        return (
                                            <tr key={idx} className={`${isSelected ? 'bg-blue-50/50' : ''} hover:bg-gray-50 transition-colors`}>
                                                <td className="px-3 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleVariantSelection(variant, idx)}
                                                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                    />
                                                </td>
                                                {Object.entries(variant)
                                                    .filter(([k]) => !['imageName', 'imagePath', 'imageUploaded', 'id'].includes(k))
                                                    .map(([k, v], i) => (
                                                        <td key={i} className="px-3 py-3 text-sm text-gray-700 font-medium">{v}</td>
                                                    ))
                                                }
                                                <td className="px-3 py-3 text-sm text-gray-900 font-bold">
                                                    {selectedProductForVariant.SalesPrice || 0}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        disabled={!isSelected}
                                                        value={isSelected ? (currentV?.quantity || 1) : 1}
                                                        onChange={(e) => updateVariantQuantity(uniqueId, e.target.value)}
                                                        className={`w-20 px-2 py-1 border rounded text-sm outline-none transition-all ${isSelected ? 'border-blue-400 focus:ring-2 focus:ring-blue-500/20' : 'border-gray-200 bg-gray-50'}`}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                onClick={() => setIsVariantModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleConfirmVariants}
                                className="px-6 py-2 text-sm font-bold text-white bg-blue-500 hover:bg-blue-600 rounded-md shadow-sm transition"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isDescEditModalOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50/50">
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">Edit Item Description</h3>
                                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mt-0.5">Item: {formData.items[editingDescIndex]?.name}</p>
                            </div>
                            <button onClick={() => setIsDescEditModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                                <Plus className="w-5 h-5 rotate-45" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg border shadow-sm">
                                        <button onClick={() => applyFormatting('B')} title="Bold (*)" className="p-2 text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm rounded transition-all"><Bold className="w-4 h-4" /></button>
                                        <button onClick={() => applyFormatting('I')} title="Italic (--)" className="p-2 text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm rounded transition-all"><Italic className="w-4 h-4" /></button>
                                        <button onClick={() => applyFormatting('U')} title="Underline (_)" className="p-2 text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm rounded transition-all"><Underline className="w-4 h-4" /></button>
                                        <button onClick={() => applyFormatting('S')} title="Strikethrough (~)" className="p-2 text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm rounded transition-all font-bold">~</button>
                                        <button onClick={() => applyFormatting('C')} title="Code (```)" className="p-2 text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm rounded transition-all"><Code className="w-4 h-4" /></button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Type className="w-3.5 h-3.5 text-gray-400" />
                                        <select
                                            onChange={(e) => insertPlaceholder(e.target.value)}
                                            className="text-xs border-gray-200 rounded-md focus:ring-blue-500 py-1.5 pl-2 pr-8 font-medium text-gray-600"
                                            value=""
                                        >
                                            <option value="">Choose Placeholder</option>
                                            {placeholderOptions.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <textarea
                                    id="txtItemDescription"
                                    value={descTempText}
                                    onChange={(e) => setDescTempText(e.target.value)}
                                    className="w-full h-40 p-4 text-sm border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:ring-0 outline-none resize-none bg-gray-50/30 transition-all font-mono"
                                    placeholder="Enter detailed description with formatting tags..."
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-gray-500">
                                    <Braces className="w-4 h-4" />
                                    <h4 className="text-xs font-bold uppercase tracking-widest">Live Preview</h4>
                                </div>
                                <div
                                    className="w-full min-h-[100px] max-h-40 overflow-y-auto p-4 bg-blue-50/30 border border-blue-100/50 rounded-xl text-sm leading-relaxed text-gray-700"
                                    dangerouslySetInnerHTML={{ __html: itemPreviewHandler(descTempText) }}
                                />
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                            <button
                                onClick={() => setIsDescEditModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApplyDesc}
                                className="px-8 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-lg shadow-blue-500/30 transition-all active:scale-95"
                            >
                                Apply Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isAddProductOpen && (
                <AddProductForm
                    source="Quotation"
                    initialName={itemSearchText}
                    onClose={() => setIsAddProductOpen(false)}
                    onSuccess={(newProd) => {
                        setIsAddProductOpen(false);
                        // Resolve the correct ID (formData.ProductID is '0' string, real ID comes from response)
                        const resolvedId = newProd.Id || parseInt(newProd.ProductID) || 0;
                        const injected = {
                            Id: resolvedId,
                            Name: newProd.ProductName || newProd.Name || '',
                            SalePrice: parseFloat(newProd.SalesPrice || newProd.Price || 0),
                            Sku: newProd.SKU || '',
                            IsVariant: 0,
                            ProductJSON: JSON.stringify({
                                HSNCode: newProd.HSNCode || '',
                                SACCode: newProd.SACCode || '',
                                IsTaxable: newProd.IsTaxable === '1' || newProd.IsTaxable === true || newProd.IsTaxable === 1
                            })
                        };
                        // Store in ref so it persists across all future searches
                        recentlyAddedProductsRef.current = [
                            injected,
                            ...recentlyAddedProductsRef.current.filter(p => p.Id !== resolvedId)
                        ];
                        // Immediately show in the open search panel
                        setShowItemSearch(true);
                        setItemSuggestions(prev => [
                            injected,
                            ...prev.filter(p => p.Id !== resolvedId)
                        ]);
                    }}
                />
            )}
        </div>
    );
});

export default AddQuotationForm;
